import { useTranslation } from 'react-i18next';
import { formatHour } from './dateUtils';
import { TimelineRow } from './types';
import { useTimelineView } from './TimelineViewContext';
import { getTimelineHourRows } from './timelineViewUtils';

export const useTimelineRows = () => {
    const { i18n } = useTranslation();
    const {
        getTasksForDay, getTasksForMonth, getTasksForYear, onCreateFlexibleTask,
        onHourPointerDown, onScaleChange, onSelectedDateChange, timelineScale,
    } = useTimelineView();
    return (periodDate: Date): TimelineRow[] => {
        if (timelineScale === 'day') {
            return getTimelineHourRows().map((row) => ({
                key: `${periodDate.toISOString()}-${row.startHour}`, date: periodDate,
                hour: row.startHour, endHour: row.endHour,
                label: formatHour(row.startHour),
                onPointerDown: (event) => onHourPointerDown(periodDate, row.startHour, event),
                isCurrent: periodDate.toDateString() === new Date().toDateString()
                    && new Date().getHours() >= row.startHour && new Date().getHours() < row.endHour,
            }));
        }
        if (timelineScale === 'month') {
            const year = periodDate.getFullYear();
            const month = periodDate.getMonth();
            return Array.from({ length: new Date(year, month + 1, 0).getDate() }, (_, index) => {
                const date = new Date(year, month, index + 1);
                return {
                    key: date.toISOString(), date,
                    label: `${date.toLocaleDateString(i18n.language, { day: 'numeric' })}, ${date.toLocaleDateString(i18n.language, { weekday: 'short' })}`,
                    tasks: getTasksForDay(date),
                    onOpen: () => { onSelectedDateChange(date); onScaleChange('day', date); },
                    onCreateFlexible: () => onCreateFlexibleTask(date, 'day'),
                    isCurrent: date.toDateString() === new Date().toDateString(),
                };
            });
        }
        if (timelineScale === 'year') {
            const year = periodDate.getFullYear();
            return Array.from({ length: 12 }, (_, month) => {
                const date = new Date(year, month, 1);
                return {
                    key: date.toISOString(), date,
                    label: date.toLocaleDateString(i18n.language, { month: 'short' }),
                    tasks: getTasksForMonth(date),
                    onOpen: () => { onSelectedDateChange(date); onScaleChange('month', date); },
                    onCreateFlexible: () => onCreateFlexibleTask(date, 'month'),
                    isCurrent: year === new Date().getFullYear() && month === new Date().getMonth(),
                };
            });
        }
        const startYear = periodDate.getFullYear() - 5;
        return Array.from({ length: 12 }, (_, index) => {
            const year = startYear + index;
            const date = new Date(year, 0, 1);
            return {
                key: year.toString(), date, label: year.toString(), tasks: getTasksForYear(year),
                onOpen: () => { onSelectedDateChange(date); onScaleChange('year', date); },
                onCreateFlexible: () => onCreateFlexibleTask(date, 'year'),
                isCurrent: year === new Date().getFullYear(),
            };
        });
    };
};
